import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type {
  EstadoDeExoneracion,
  LineaDelReporteDeExoneraciones,
  ReporteDeExoneraciones as Reporte,
} from '@/shared/api/tipos';
import { ESTADOS } from './estadosDeExoneracion';

export type Filtros = { texto: string; tipo: string; tipoNombre?: string; estado: string };

export function ReporteDeExoneraciones({
  filtros,
  alCerrar,
}: {
  filtros: Filtros;
  alCerrar: () => void;
}) {
  const reporte = useQuery({
    queryKey: ['reporte-exoneraciones', filtros.texto, filtros.tipo, filtros.estado],
    queryFn: () =>
      api.get<Reporte>('/exoneraciones/reporte', {
        query: {
          texto: filtros.texto || undefined,
          tipo: filtros.tipo || undefined,
          estado: filtros.estado || undefined,
        },
      }),
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fondo-del-dialogo absolute inset-0 bg-armazon-900/40 backdrop-blur-sm"
        aria-hidden
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Reporte de exoneraciones"
        className="para-imprimir para-imprimir-largo relative flex max-h-[95dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="sin-imprimir flex shrink-0 items-center justify-between gap-4 border-b border-armazon-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              disabled={!reporte.data}
              title="Imprimir reporte"
              aria-label="Imprimir reporte"
              className="grid size-10 place-items-center rounded-lg bg-marca-600 text-white transition-colors hover:bg-marca-700 disabled:opacity-40"
            >
              <Printer className="size-[18px]" aria-hidden />
            </button>
            <span className="text-[11px] uppercase text-armazon-400">
              O guárdelo como PDF desde el diálogo de impresión
            </span>
          </div>

          <button
            type="button"
            onClick={alCerrar}
            aria-label="Cerrar"
            className="grid size-8 shrink-0 place-items-center rounded-lg text-armazon-400 transition-colors hover:bg-armazon-100 hover:text-armazon-700"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
          {reporte.isPending && (
            <p className="py-16 text-center text-sm uppercase text-armazon-400">
              Preparando el reporte…
            </p>
          )}

          {reporte.isError && (
            <p role="alert" className="py-16 text-center text-sm text-red-700">
              NO SE PUDO GENERAR EL REPORTE
            </p>
          )}

          {reporte.data && <Documento reporte={reporte.data} filtros={filtros} />}
        </div>
      </div>
    </div>
  );
}

function Documento({
  reporte,
  filtros,
}: {
  reporte: Reporte;
  filtros: Filtros;
}) {
  const aplicados = [
    filtros.texto ? `Beneficiario: «${filtros.texto}»` : null,
    filtros.tipo ? `Tipo: ${filtros.tipoNombre ?? filtros.tipo.replace(/_/g, ' ')}` : null,
    filtros.estado ? `Estado: ${nombreDelEstado(filtros.estado)}` : null,
  ].filter(Boolean);

  return (
    <>
      <header className="mb-6 border-b-2 border-armazon-900 pb-4 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-armazon-500">
          EPAGAL EP
        </p>
        <p className="text-[10px] uppercase tracking-wide text-armazon-400">
          Empresa Pública de Aseo y Gestión Ambiental del Cantón Latacunga
        </p>
        <h1 className="mt-4 text-lg font-bold uppercase tracking-wide text-armazon-900">
          Reporte de exoneraciones
        </h1>
        <p className="mt-1 text-xs uppercase text-armazon-500">
          Al corte del {formatearFecha(reporte.corte)} · {reporte.lineas.length}{' '}
          {reporte.lineas.length === 1 ? 'registro' : 'registros'}
        </p>
      </header>

      <p className="mb-4 rounded-lg border border-armazon-200 bg-armazon-50 px-4 py-2 text-[11px] uppercase text-armazon-600">
        <span className="font-semibold">Filtros aplicados:</span>{' '}
        {aplicados.length > 0 ? aplicados.join(' · ') : 'Ninguno — se listan todas'}
      </p>

      {reporte.truncado && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-[11px] uppercase text-amber-900"
        >
          Se listan las primeras {reporte.tope.toLocaleString('es-EC')} de{' '}
          {reporte.resumen.total.toLocaleString('es-EC')}. Los importes de abajo solo suman las
          listadas: acote la búsqueda para tener el total.
        </p>
      )}

      <div className="mb-5 grid grid-cols-4 gap-3">
        <Indicador
          valor={String(reporte.resumen.vigentes)}
          rotulo="Vigentes hoy"
          nota="Aprobadas y dentro de fechas"
          destacado
        />
        <Indicador
          valor={`$${reporte.resumen.exoneradoAlMes.toFixed(2)}`}
          rotulo="Se deja de cobrar al mes"
          nota="Solo por las vigentes"
          destacado
        />
        <Indicador
          valor={`$${reporte.resumen.cobrableAlMes.toFixed(2)}`}
          rotulo="Se cobra al mes"
          nota="A esas mismas cuentas"
        />
        <Indicador
          valor={String(reporte.resumen.pendientes)}
          rotulo="Pendientes"
          nota="Se les factura completo"
        />
      </div>

      <div className="mb-5 grid grid-cols-4 gap-3 text-center">
        <Contador valor={reporte.resumen.aprobadas} rotulo="Aprobadas" />
        <Contador valor={reporte.resumen.pendientes} rotulo="Pendientes" />
        <Contador valor={reporte.resumen.rechazadas} rotulo="Rechazadas" />
        <Contador valor={reporte.resumen.suspendidas} rotulo="Suspendidas" />
      </div>

      <table className="w-full border-collapse text-[11px]">
        <thead>
          <tr className="bg-armazon-800 text-white">
            <Encabezado className="w-8 text-center">#</Encabezado>
            <Encabezado>Beneficiario</Encabezado>
            <Encabezado>Cédula / RUC</Encabezado>
            <Encabezado>Cuenta</Encabezado>
            <Encabezado>Tipo</Encabezado>
            <Encabezado className="text-center">%</Encabezado>
            <Encabezado>Documento</Encabezado>
            <Encabezado>Desde</Encabezado>
            <Encabezado>Hasta</Encabezado>
            <Encabezado>Estado</Encabezado>
            <Encabezado className="text-right">Tasa / mes</Encabezado>
            <Encabezado className="text-right">Descuento / mes</Encabezado>
          </tr>
        </thead>
        <tbody>
          {reporte.lineas.map((l, i) => (
            <Fila key={l.id} linea={l} numero={i + 1} />
          ))}

          {reporte.lineas.length === 0 && (
            <tr>
              <td
                colSpan={12}
                className="border-b border-armazon-200 py-10 text-center uppercase text-armazon-400"
              >
                Ninguna exoneración coincide con los filtros
              </td>
            </tr>
          )}
        </tbody>

        {reporte.lineas.length > 0 && (
          <tfoot>
            <tr className="bg-armazon-100 font-semibold uppercase text-armazon-800">
              <td colSpan={10} className="px-2 py-2 text-right">
                Total de las {reporte.resumen.vigentes} vigentes
              </td>
              <td className="importe px-2 py-2 text-right">
                ${reporte.resumen.tasaDeLasVigentes.toFixed(2)}
              </td>
              <td className="importe px-2 py-2 text-right">
                ${reporte.resumen.exoneradoAlMes.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      <div className="mt-6 border-t border-armazon-200 pt-3 text-[10px] leading-relaxed text-armazon-500">
        <p className="text-justify uppercase">
          Los importes se calculan sobre el catastro vigente al corte —tasa de cada cuenta sobre
          una base de ${reporte.valorDeReferencia.toFixed(2)}—, y expresan lo que se dejará de
          cobrar en la próxima emisión si nada cambia. No son lo efectivamente descontado en
          facturas ya emitidas.
        </p>
        <p className="mt-2 text-justify uppercase">
          Solo descuentan las exoneraciones aprobadas y dentro de sus fechas de vigencia. Una
          solicitud pendiente figura en el listado pero se factura completa hasta que se aprueba.
        </p>
      </div>
    </>
  );
}

function Fila({ linea, numero }: { linea: LineaDelReporteDeExoneraciones; numero: number }) {
  const estado = ESTADOS[linea.estado];

  return (
    <tr className="border-b border-armazon-100 align-top even:bg-armazon-50/60">
      <td className="px-2 py-1.5 text-center text-armazon-400">{numero}</td>
      <td className="px-2 py-1.5 font-medium uppercase text-armazon-800">{linea.cliente}</td>
      <td className="importe px-2 py-1.5 text-armazon-600">{linea.identificacion}</td>
      <td className="importe px-2 py-1.5 text-armazon-600">
        {linea.general ? (
          <span className="uppercase text-armazon-500">Todas</span>
        ) : (
          (linea.cuenta ?? '—')
        )}
      </td>
      <td className="px-2 py-1.5 uppercase text-armazon-700">
        {linea.tipo}
        {linea.gradoDiscapacidad != null && (

          <span className="block text-[9px] normal-case text-armazon-400">
            Grado del carné: {linea.gradoDiscapacidad}%
          </span>
        )}
      </td>
      <td className="importe px-2 py-1.5 text-center font-semibold text-armazon-800">
        {linea.porcentaje}%
      </td>
      <td className="px-2 py-1.5 text-armazon-600">{linea.documentoRespaldo ?? '—'}</td>
      <td className="importe px-2 py-1.5 text-armazon-600">{formatearCorto(linea.desde)}</td>
      <td className="importe px-2 py-1.5 text-armazon-600">
        {linea.hasta ? (
          formatearCorto(linea.hasta)
        ) : (
          <span className="uppercase text-armazon-400">Vigencia indefinida</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        <span className="uppercase text-armazon-700">{estado.etiqueta}</span>
        {!linea.vigente && estado.descuenta && (

          <span className="block text-[9px] uppercase text-amber-600">Fuera de vigencia</span>
        )}
      </td>
      <td className="importe px-2 py-1.5 text-right text-armazon-700">
        ${linea.tasaMensual.toFixed(2)}
      </td>
      <td
        className={`importe px-2 py-1.5 text-right font-semibold ${
          linea.descuentoMensual > 0 ? 'text-emerald-700' : 'text-armazon-300'
        }`}
      >
        ${linea.descuentoMensual.toFixed(2)}
      </td>
    </tr>
  );
}

function Encabezado({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Indicador({
  valor,
  rotulo,
  nota,
  destacado = false,
}: {
  valor: string;
  rotulo: string;
  nota: string;
  destacado?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2.5 ${
        destacado ? 'border-marca-200 bg-marca-50' : 'border-armazon-200 bg-white'
      }`}
    >
      <p
        className={`importe text-lg font-bold ${
          destacado ? 'text-marca-700' : 'text-armazon-800'
        }`}
      >
        {valor}
      </p>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-600">
        {rotulo}
      </p>
      <p className="text-[9px] uppercase text-armazon-400">{nota}</p>
    </div>
  );
}

function Contador({ valor, rotulo }: { valor: number; rotulo: string }) {
  return (
    <div className="rounded-lg border border-armazon-200 px-3 py-1.5">
      <span className="importe text-sm font-semibold text-armazon-800">{valor}</span>{' '}
      <span className="text-[10px] uppercase text-armazon-500">{rotulo}</span>
    </div>
  );
}

function nombreDelEstado(codigo: string): string {
  return codigo in ESTADOS ? ESTADOS[codigo as EstadoDeExoneracion].etiqueta : codigo;
}

function formatearFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  const MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${dia} de ${MESES[Number(mes) - 1] ?? mes} de ${anio}`;
}

function formatearCorto(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
